import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import NotificationCenter from '@/components/shared/NotificationCenter';
import {
  House, ChartLineUp, Lightning, Storefront, CreditCard,
  ShieldWarning, Package, Brain, SignOut, List, TreeStructure, Plugs, FileText, X, Palette, CurrencyDollar, Gear, Timer, UploadSimple, Globe, Webhook, Flask, ClipboardText, Users, ShieldCheck,
  UserCircle, Compass, AmazonLogo, Stack, VideoCamera, YoutubeLogo, Broadcast, Megaphone, Scales, GlobeHemisphereWest, BookOpen
} from '@phosphor-icons/react';

var adminNav = [
  { label: 'Command Center', path: '/admin', icon: House },
  { label: 'Traffic Intelligence', path: '/analytics', icon: ChartLineUp },
  { label: 'Discover', path: '/discover', icon: Compass },
  { label: 'Offer Marketplace', path: '/marketplace', icon: Storefront },
  { label: 'Accounts', path: '/accounts', icon: Users },
  { label: 'Compliance', path: '/compliance', icon: Scales },
  { label: 'Fraud Detection', path: '/fraud', icon: ShieldWarning },
  { label: 'Payouts', path: '/payouts', icon: CreditCard },
  { label: 'Scheduling', path: '/scheduling', icon: Timer },
  { label: 'Reports', path: '/reports', icon: FileText },
  { label: 'Webhooks', path: '/webhooks', icon: Plugs },
  { label: 'AI Insights', path: '/ai-insights', icon: Brain },
  { label: 'Audit Log', path: '/audit-log', icon: ClipboardText },
  { label: 'White-label', path: '/settings/whitelabel', icon: Palette },
  { label: 'Domains', path: '/domains', icon: Globe },
  { label: 'Global', path: '/global-settings', icon: GlobeHemisphereWest },
  { label: 'Documentation', path: '/docs', icon: BookOpen },
];

var affiliateNav = [
  { label: 'Dashboard', path: '/affiliate', icon: House },
  { label: 'Creator Studio', path: '/creator-studio', icon: UserCircle },
  { label: 'Video Library', path: '/videos', icon: VideoCamera },
  { label: 'YouTube Embeds', path: '/youtube', icon: YoutubeLogo },
  { label: 'Live Streaming', path: '/live', icon: Broadcast },
  { label: 'Discover', path: '/discover', icon: Compass },
  { label: 'Marketplace', path: '/marketplace', icon: Storefront },
  { label: 'Smart Links', path: '/affiliate/links', icon: Lightning },
  { label: 'Amazon Associates', path: '/amazon', icon: AmazonLogo },
  { label: 'Premium Plans', path: '/sponsored', icon: CreditCard },
  { label: 'Sub-affiliates', path: '/sub-affiliates', icon: TreeStructure },
  { label: 'Reports', path: '/reports', icon: FileText },
  { label: 'Global Settings', path: '/global-settings', icon: GlobeHemisphereWest },
  { label: 'Documentation', path: '/docs', icon: BookOpen },
  { label: 'Payouts', path: '/payouts', icon: CreditCard },
];

var advertiserNav = [
  { label: 'Dashboard', path: '/advertiser', icon: House },
  { label: 'My Offers', path: '/advertiser/offers', icon: Package },
  { label: 'Sponsored', path: '/sponsored', icon: Megaphone },
  { label: 'Postbacks', path: '/postbacks', icon: Plugs },
  { label: 'Reports', path: '/reports', icon: FileText },
  { label: 'Currency', path: '/settings/currency', icon: CurrencyDollar },
  { label: 'Analytics', path: '/analytics', icon: ChartLineUp },
  { label: 'Documentation', path: '/docs', icon: BookOpen },
];

export default function Layout({ children }) {
  var auth = useAuth();
  var user = auth.user;
  var logout = auth.logout;
  var navigate = useNavigate();
  var location = useLocation();
  var [collapsed, setCollapsed] = useState(false);
  var [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile sidebar on route change
  useEffect(function () { setMobileOpen(false); }, [location.pathname]);

  var navItems = user?.role === 'admin' ? adminNav :
    user?.role === 'advertiser' ? advertiserNav : affiliateNav;

  var handleNav = function (path) {
    navigate(path);
    setMobileOpen(false);
  };

  var sidebarContent = (
    <>
      <div className="h-14 px-4 border-b border-zinc-800 flex items-center gap-3 shrink-0">
        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-sm shrink-0">DW</div>
        <span className="font-heading font-bold text-lg text-white truncate">Dynamic World</span>
        <button className="ml-auto md:hidden text-zinc-400 hover:text-white" onClick={function () { setMobileOpen(false); }}>
          <X size={20} />
        </button>
      </div>
      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
        {navItems.map(function (item) {
          var isActive = location.pathname === item.path;
          var Icon = item.icon;
          return (
            <button
              key={item.path}
              data-testid={'nav-' + (item.path.replace(/\//g, '-').slice(1) || 'home')}
              onClick={function () { handleNav(item.path); }}
              className={'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ' +
                (isActive
                  ? 'bg-blue-600/15 text-blue-400 border border-blue-500/20'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60 border border-transparent')}
            >
              <Icon size={20} weight={isActive ? 'duotone' : 'regular'} className="shrink-0" />
              <span className="truncate">{item.label}</span>
            </button>
          );
        })}
      </nav>
      <div className="p-2 border-t border-zinc-800 shrink-0">
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-zinc-400 hover:text-red-400 hover:bg-zinc-800/60 transition-colors"
        >
          <SignOut size={20} className="shrink-0" />
          <span>Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-zinc-950 text-zinc-100 overflow-hidden">
      {/* Desktop sidebar */}
      <aside className={'hidden md:flex shrink-0 border-r border-zinc-800 bg-zinc-950 flex-col transition-all duration-200 ' + (collapsed ? 'w-16' : 'w-64')}>
        {collapsed ? (
          <>
            <div className="h-14 px-4 border-b border-zinc-800 flex items-center justify-center">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-sm">DW</div>
            </div>
            <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
              {navItems.map(function (item) {
                var isActive = location.pathname === item.path;
                var Icon = item.icon;
                return (
                  <button key={item.path} onClick={function () { navigate(item.path); }} title={item.label}
                    className={'w-full flex items-center justify-center p-2.5 rounded-lg transition-all ' +
                      (isActive ? 'bg-blue-600/15 text-blue-400' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60')}>
                    <Icon size={20} weight={isActive ? 'duotone' : 'regular'} />
                  </button>
                );
              })}
            </nav>
            <div className="p-2 border-t border-zinc-800">
              <button onClick={logout} title="Logout" className="w-full flex items-center justify-center p-2.5 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-zinc-800/60">
                <SignOut size={20} />
              </button>
            </div>
          </>
        ) : sidebarContent}
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={function () { setMobileOpen(false); }} />
          <aside className="relative w-72 max-w-[85vw] bg-zinc-950 border-r border-zinc-800 flex flex-col z-10 animate-in slide-in-from-left duration-200">
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="h-12 md:h-14 shrink-0 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800 px-3 md:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button data-testid="toggle-sidebar" onClick={function () { if (window.innerWidth < 768) { setMobileOpen(true); } else { setCollapsed(!collapsed); } }}
              className="text-zinc-400 hover:text-white transition-colors p-1 -ml-1">
              <List size={22} />
            </button>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <NotificationCenter />
            <span className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold hidden sm:block">{user?.role}</span>
            <div className="flex items-center gap-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 md:w-8 md:h-8 rounded-full" />
              ) : (
                <div className="w-7 h-7 md:w-8 md:h-8 bg-zinc-800 rounded-full flex items-center justify-center text-xs md:text-sm font-medium text-blue-400">
                  {user?.name?.[0] || 'U'}
                </div>
              )}
              <span className="text-sm text-zinc-300 hidden sm:block max-w-[120px] truncate">{user?.name}</span>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-3 md:p-6 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}
